import React, { useContext, useEffect, useRef, useState } from 'react';

import classnames from 'classnames';
import { ArrowRight, FileCheck2, Merge, MoreHorizontal, Plus } from 'lucide-react';

import { reloadBoard } from 'common/actions/boards';
import { invalidateDashboardActivity } from 'common/actions/dashboardActivity';
import { deletePostDraft, savePostDraft } from 'common/actions/postDraft';
import { reloadOnMerge } from 'common/actions/postMerge';
import { invalidatePostQueries } from 'common/actions/postQueries';
import { reloadPost } from 'common/actions/posts';
import { invalidateSuggestions } from 'common/actions/postSuggestions';
import {
  type QueryParams,
  invalidateQueriesAndReload,
  loadMorePosts,
  removeItem,
} from 'common/actions/queueItemQueries';
import { invalidateRoadmap } from 'common/actions/roadmap';
import {
  reloadRoadmapPostsForRoadmapsWithPost,
  removePostFromAllRoadmaps,
} from 'common/actions/roadmapPosts';
import AJAX from 'common/AJAX';
import { type QueueItem, type QueuePost } from 'common/api/endpoints/queue';
import Portal from 'common/common/Portal';
import Tooltip from 'common/common/Tooltip';
import { convertMRRToTimeframe } from 'common/company/RevenueHelpers';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { getEncodedQueryParams } from 'common/containers/QueuePostItemListContainer';
import RoadmapPostContainer from 'common/containers/RoadmapPostContainer';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import connect from 'common/core/connect';
import useBackgroundClick from 'common/hooks/useBackgroundClick';
import { KeyIdentifiers } from 'common/KeyCodes';
import Link from 'common/Link';
import ModalPortal from 'common/modals/ModalPortal';
import ModernConfirmModal from 'common/modals/ModernConfirmModal';
import MergePostDropdown from 'common/post/MergePostDropdown';
import SelectableDataTable from 'common/SelectableDataTable';
import ActionMenu from 'common/subdomain/admin/ActionMenu';
import AdminFeedbackPost from 'common/subdomain/admin/AdminFeedbackPost';
import ButtonV2 from 'common/ui/ButtonV2';
import IconButtonV2 from 'common/ui/IconButtonV2';
import { P } from 'common/ui/Text';
import { isNotNil } from 'common/util/isNil';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';

import AdminQueueNUX from './AdminQueueNUX';
import AdminQueuePost from './AdminQueuePost';

import 'css/components/subdomain/admin/_AdminQueuePage.scss';

import type { Board } from 'common/api/endpoints/boards';
import type { Company, ViewerPreferences } from 'common/api/endpoints/companies';
import type { Roadmap } from 'common/api/endpoints/roadmaps';
import type { StrippedUser } from 'common/api/endpoints/users';
import type { CustomPostField } from 'common/api/resources/postFields';
import type { Post } from 'common/api/resources/posts';
import type { Column, Sort } from 'common/DataTable';
import type { Dispatch } from 'redux-connect';

type InboxItem = QueuePost & {
  author: StrippedUser;
  board: Board;
};

type Row = {
  id: string;
  source: string;
  item: InboxItem;
  actions: {
    approve: () => Promise<void>;
    delete: () => Promise<void>;
    merge: (post: Post) => Promise<void>;
  };
};

type SortableColumn<R extends Row, ID extends keyof R> = Column<R, ID> & {
  sort: (order: 'asc' | 'desc') => void;
};

type PostModalDraft = {
  title: string;
  details: string | null;
  internalComment: string | null;
  author: StrippedUser;
};

type Props = {
  invalidateQueriesAndReload: (queryParams: QueryParams) => void;
  loadMoreItems: (queryParams: QueryParams, pages: number) => void;
  removeItem: (item: QueueItem) => void;
  removePost: (post: Post, board: Board) => void;
  reloadOnMerge: (fromPost: Post, intoPost: Post, fromBoard: Board, intoBoard: Board) => void;
  reloadPost: (post: Post, queryParams?: QueryParams) => void;
  boards: Board[];
  customPostFields: CustomPostField[];
  queuePostItemList: {
    hasMore: boolean;
    items: InboxItem[];
    loading: boolean;
    error?: string;
    pages?: number;
    loadingMore: boolean;
    queryParams: QueryParams;
  };
  roadmaps: Roadmap[];
};

enum Modal {
  merge = 'merge',
  delete = 'delete',
}

enum Action {
  approve = 'approve',
  merge = 'merge',
  delete = 'delete',
}

type ActionsCellProps = {
  row: Row;
  boards: Board[];
};

const ActionsCell = ({ row, boards }: ActionsCellProps) => {
  const [modal, setModal] = useState<Modal | null>(null);
  const [isMenuOpen, setMenuOpen] = useState<boolean>(false);
  const [loadingAction, setLoadingAction] = useState<Action | null>();
  const mergeRef = useRef<HTMLButtonElement>(null);
  const menuPortalRef = useRef<Portal>(null);
  const portalRef = useRef<Portal>(null);

  useBackgroundClick(() => {
    if (modal === Modal.merge) {
      setModal(null);
      setMenuOpen(false);
    }
  }, [portalRef, mergeRef]);

  useBackgroundClick(() => {
    if (isMenuOpen) {
      setMenuOpen(false);
      setModal(null);
    }
  }, [menuPortalRef, mergeRef]);

  const toggleMenu = () => {
    setModal(null);
    setMenuOpen(!isMenuOpen);
  };

  const openMergeModal = () => {
    setMenuOpen(false);
    setModal(Modal.merge);
  };

  const openDeleteModal = () => {
    setMenuOpen(false);
    setModal(Modal.delete);
  };

  const performDelete = async () => {
    setLoadingAction(Action.delete);
    await row.actions.delete();
    setLoadingAction(null);
  };

  const performApprove = async () => {
    setLoadingAction(Action.approve);
    await row.actions.approve();
    setLoadingAction(null);
  };

  const performMerge = async (post: Post) => {
    setLoadingAction(Action.merge);
    await row.actions.merge(post);
    setLoadingAction(null);
  };

  const { duplicatePost } = row.item;

  return (
    <div className="queueActions">
      {duplicatePost ? (
        <ButtonV2
          disabled={loadingAction === Action.merge}
          loading={loadingAction === Action.merge}
          className="merge"
          aria-label="merge"
          onClick={() => performMerge(duplicatePost)}
          size="medium"
          variant="plain"
          startIcon={Merge}>
          Merge posts
        </ButtonV2>
      ) : (
        <ButtonV2
          disabled={loadingAction === Action.approve}
          loading={loadingAction === Action.approve}
          className="approve"
          aria-label="approve"
          onClick={performApprove}
          size="medium"
          variant="plain"
          startIcon={FileCheck2}>
          Approve post
        </ButtonV2>
      )}
      <div className="verticalSpacer"></div>
      <Tooltip delay={1000} value="More actions" position="left">
        <IconButtonV2
          icon={MoreHorizontal}
          ref={mergeRef}
          aria-label="More actions"
          size="medium"
          variant="plain"
          onClick={toggleMenu}
        />
      </Tooltip>
      {isMenuOpen && (
        <ActionMenu
          className="moreActionsPopover"
          align="end"
          ref={menuPortalRef}
          relativeToRef={mergeRef}
          options={[
            row.item.duplicatePost && {
              action: performApprove,
              label: 'Keep as separate post',
            },
            {
              action: openMergeModal,
              label: 'Merge with other post',
            },
            {
              action: openDeleteModal,
              destructive: true,
              label: 'Delete post',
            },
          ].filter(isNotNil)}
        />
      )}
      {modal === Modal.delete && (
        <ModernConfirmModal
          onClose={() => setModal(null)}
          onConfirm={performDelete}
          confirmText="Delete post"
          type="destructive"
          header="Are you sure you want to delete this post?">
          <P>
            The post will no longer be visible to you or other users in your workspace once deleted.
            This action cannot be&nbsp;reversed.
          </P>
        </ModernConfirmModal>
      )}
      {modal === Modal.merge && (
        <Portal
          align="end"
          className="modalPostDropdownPortal"
          zIndex={100}
          ref={portalRef}
          relativeToRef={mergeRef}>
          <MergePostDropdown
            autoFocus={true}
            board={row.item.board}
            boards={boards}
            onPostSelected={performMerge}
            post={row.item}
          />
        </Portal>
      )}
    </div>
  );
};

const AdminQueuePosts = ({
  boards,
  customPostFields,
  queuePostItemList,
  loadMoreItems,
  roadmaps,
  reloadPost,
  reloadOnMerge,
  removePost,
  removeItem,
}: Props) => {
  const company = useContext<Company>(CompanyContext);
  const showToast = useContext(ShowToastContext);
  const location = useContext(LocationContext);
  const router = useContext(RouterContext);

  const initialPathname = useRef<string>(location.pathname);
  const initialViewerPreferences = useRef<ViewerPreferences>(company?.viewerPreferences);

  // load pre-saved filters
  useEffect(() => {
    const lastUsedFilters = initialViewerPreferences.current?.queuePostFilters;
    if (!lastUsedFilters) {
      return;
    }

    router.replace({
      pathname: initialPathname.current,
      query: getEncodedQueryParams(lastUsedFilters),
    });
  }, [router]);

  // expand post logic
  const getSelectedPost = () => {
    if (!location.query.postURLName) {
      return;
    }

    const potentialPosts = queuePostItemList.items
      .map((item) => [item, item.duplicatePost])
      .flat()
      .filter(isNotNil);

    return potentialPosts.find((post) => post.urlName === location.query.postURLName);
  };

  const selectedPost = getSelectedPost();
  const closePost = () =>
    router.replace({
      pathname: location.pathname,
      query: { ...location.query, postURLName: undefined },
    });

  // table setup
  const columns = [
    {
      id: 'item',
      align: 'left',
      header: 'Details',
      sortable: true,
      fullWidth: true,
      sort: (order) => {
        router.replace({
          pathname: location.pathname,
          query: { ...location.query, sort: 'created', sortDirection: order },
        });
      },
      cell: (item) => {
        if (!item.duplicatePost) {
          return (
            <button
              className="unstyledButton"
              onClick={() =>
                router.replace({
                  pathname: location.pathname,
                  query: { ...location.query, postURLName: item.urlName },
                })
              }>
              <AdminQueuePost
                className="queueItem"
                title={item.title}
                details={item.details}
                author={item.author}
                created={item.created}
                board={item.board}
              />
            </button>
          );
        } else if (item.duplicatePost) {
          const duplicatePost = item.duplicatePost;
          const board = boards.find((board) => board._id === duplicatePost.boardID);
          return (
            <div className="duplicateQueueItem">
              <button
                className="unstyledButton leftItem"
                onClick={() =>
                  router.replace({
                    pathname: location.pathname,
                    query: { ...location.query, postURLName: item.urlName },
                  })
                }>
                <AdminQueuePost
                  className="queueItem"
                  title={item.title}
                  details={item.details}
                  author={item.author}
                  created={item.created}
                  board={item.board}
                />
              </button>
              <ArrowRight className="mergeButton" strokeWidth={1} width={32} />
              <button
                className="unstyledButton rightItem"
                onClick={() =>
                  router.replace({
                    pathname: location.pathname,
                    query: { ...location.query, postURLName: item.duplicatePost?.urlName },
                  })
                }>
                <AdminQueuePost
                  className="queueItem"
                  title={item.duplicatePost.title}
                  details={item.duplicatePost.details}
                  author={item.duplicatePost.author}
                  created={item.duplicatePost.created}
                  board={board}
                  meta={{
                    score: item.duplicatePost.score,
                    commentCount: item.duplicatePost.commentCount,
                    ...(company.hasCollectedMonthlySpend && {
                      monthlySpend: convertMRRToTimeframe(
                        item.duplicatePost.totalMRR,
                        company.revenueTimeframe
                      ),
                    }),
                  }}
                />
              </button>
            </div>
          );
        }

        return null;
      },
    } as SortableColumn<Row, 'item'>,
    {
      id: 'actions',
      align: 'left',
      header: 'Quick actions',
      sortable: false,
      cell: (_, row) => {
        return <ActionsCell row={row} boards={boards} />;
      },
    } as Column<Row, 'actions'>,
  ];

  const onApprovePost = async (item: InboxItem) => {
    const response = await AJAX.post('/api/queue/posts/approve', {
      postID: item._id,
    });

    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
      errors: {
        default: 'There was a problem approving this item. Try again.',
      },
    });

    if (error) {
      showToast(error.message, ToastTypes.error);
      return;
    }

    removeItem(item);
  };

  const onDeletePost = async (item: InboxItem) => {
    const response = await AJAX.post('/api/posts/delete', {
      postID: item._id,
    });

    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
      errors: {
        default: 'There was a problem rejecting this item. Try again.',
      },
    });

    if (error) {
      showToast(error.message, ToastTypes.error);
      return;
    }

    removeItem(item);
    removePost(item, item.board);
  };

  const itemToRow = (item: InboxItem) => {
    // actions
    const approve = async () => {
      await onApprovePost(item);
      showToast('Post approved successfully', ToastTypes.success);
    };

    const deleteItem = async () => {
      await onDeletePost(item);
      showToast('Post deleted successfully', ToastTypes.success);
    };

    const onMerge = async (targetPost: Post) => {
      const targetBoard = boards.find((board) => board._id === targetPost.boardID);
      if (!targetBoard) {
        showToast('Failed to find the target board', ToastTypes.error);
        return;
      }

      await onApprovePost(item);

      const response = await AJAX.post('/api/posts/merge', {
        mergePostID: item._id,
        mergePostIntoID: targetPost._id,
      });

      const { error } = parseAPIResponse(response, {
        isSuccessful: isDefaultSuccessResponse,
        errors: {
          'board deleted': 'The board for one of the posts does not exist anymore',
          'invalid company': "You can't merge posts from another company",
          'not authorized': "You don't have permission to merge posts",
          'post missing': 'One of the posts you are trying to merge does not exist anymore',
          'same posts': 'It is not possible to merge one post with itself',
        },
      });

      if (error) {
        showToast(error.message, ToastTypes.error);
        return;
      }

      reloadOnMerge(item, targetPost, item.board, targetBoard);
      showToast('Posts merged successfully', ToastTypes.success);
    };

    return {
      id: item._id,
      source: item.source,
      type: item.type,
      item,
      actions: {
        approve,
        merge: onMerge,
        delete: deleteItem,
      },
    };
  };

  const isReady = !queuePostItemList.error && !queuePostItemList.loading;
  const rows = isReady ? queuePostItemList.items.map<Row>(itemToRow) : [];

  if (isReady && !rows.length) {
    return (
      <div className="adminQueuePage empty">
        <AdminQueueNUX
          title="No new feature requests!"
          info={[
            'Canny collects incoming feature requests from all of your sources for you to review.',
            'Check back regularly for any new feedback that may require your attention.',
          ]}
          cta={
            <Link to="/admin/settings/integrations">
              <ButtonV2 startIcon={Plus} size="medium">
                Connect feedback sources
              </ButtonV2>
            </Link>
          }
        />
      </div>
    );
  }

  // map sort fields to columnIDs
  const isSortDefined = location.query.sortDirection && location.query.sort;
  const sortColumnMap: Record<string, string> = {
    source: 'source',
    created: 'item',
  };

  const onSort = ({ columnID, order }: Sort) => {
    const column = columns.find((column) => columnID === column.id);
    if (column && 'sort' in column) {
      column.sort(order);
    }
  };

  return (
    <div className="adminQueuePage">
      <SelectableDataTable<Row>
        cellAlignment="top"
        loading={queuePostItemList.loading}
        error={queuePostItemList.error}
        padding="medium"
        rounded={false}
        onSort={onSort}
        defaultSort={
          isSortDefined
            ? {
                columnID: sortColumnMap[location.query.sort],
                order: location.query.sortDirection,
              }
            : undefined
        }
        pagination={{
          hasMore: queuePostItemList.hasMore,
          loading: queuePostItemList.loadingMore,
          onLoadMore: () =>
            loadMoreItems(
              queuePostItemList.queryParams,
              queuePostItemList.pages ? queuePostItemList.pages + 1 : 2
            ),
        }}
        hotkeys={{
          [KeyIdentifiers.Space]: (_, element: HTMLElement | null) => {
            const button: HTMLElement | null = element?.querySelector('.sourceLink') ?? null;
            button?.click();
          },
          [KeyIdentifiers.N]: (_, element: HTMLElement | null) => {
            const button: HTMLElement | null =
              element?.querySelector('.queueActions > .approve') ?? null;
            button?.click();
          },
          [KeyIdentifiers.V]: (_, element: HTMLElement | null) => {
            const button: HTMLElement | null =
              element?.querySelector('.queueActions > .merge') ?? null;
            button?.click();
          },
          [KeyIdentifiers.X]: (_, element: HTMLElement | null) => {
            const button: HTMLElement | null =
              element?.querySelector('.queueActions > .delete') ?? null;
            button?.click();
          },
          [KeyIdentifiers.M]: (row: Row) => {
            if (!row.item.duplicatePost) {
              return;
            }

            row.actions.merge(row.item.duplicatePost);
          },
        }}
        columns={columns}
        rows={rows}
      />
      {selectedPost && (
        <ModalPortal
          allowBodyScroll
          allowEscape
          className={classnames('adminQueueDrawer', 'postModal', { modalVisible: !!selectedPost })}
          onClose={closePost}>
          <div className="modalContents">
            <RoadmapPostContainer boards={boards} post={selectedPost}>
              <AdminFeedbackPost
                key={selectedPost?._id}
                customPostFields={customPostFields}
                onClose={closePost}
                onDelete={() => {
                  if (!selectedPost || !selectedPost.board) {
                    return;
                  }

                  removePost(selectedPost, selectedPost.board);
                  closePost();
                }}
                onEdited={() => {
                  if (!selectedPost) {
                    return;
                  }

                  reloadPost(selectedPost, queuePostItemList.queryParams);
                  closePost();
                }}
                roadmaps={roadmaps}
                skipFixURL={true}
              />
            </RoadmapPostContainer>
          </div>
        </ModalPortal>
      )}
    </div>
  );
};

export default connect(null, (dispatch: Dispatch) => ({
  deletePostDraft: () => {
    return dispatch(deletePostDraft());
  },
  loadMoreItems: (queryParams: QueryParams, pages: number) => {
    return dispatch(loadMorePosts(queryParams, pages));
  },
  savePostDraft: (postDraft: PostModalDraft) => {
    return dispatch(savePostDraft(postDraft));
  },
  reloadPost: (post: Post, queryParams?: QueryParams) => {
    return Promise.all([
      dispatch(invalidatePostQueries()),
      dispatch(reloadPost(post)),
      queryParams && dispatch(invalidateQueriesAndReload(queryParams)),
      dispatch(reloadRoadmapPostsForRoadmapsWithPost(post)),
    ]);
  },
  reloadOnMerge: (mergePost: Post, intoPost: Post, fromBoard: Board, toBoard: Board) => {
    return dispatch(reloadOnMerge(mergePost, intoPost, fromBoard, toBoard));
  },
  removePost: (post: Post, board: Board) => {
    return Promise.all([
      dispatch(reloadBoard(board.urlName)),
      dispatch(invalidateDashboardActivity()),
      dispatch(invalidateRoadmap()),
      dispatch(invalidateSuggestions()),
      dispatch(removePostFromAllRoadmaps(post)),
      dispatch(invalidatePostQueries()),
    ]);
  },
  removeItem: (item: QueueItem) => {
    return dispatch(removeItem(item));
  },
}))(AdminQueuePosts);
